import { Icon, Menu, IconButton } from '@sanlam-glacier/app-common/components';
import { useState } from 'react';
import { OverviewContextMenuItem } from './OverviewContextMenuItem';
import { SecurityAccessFeaturesEnum } from 'bff';
import { useUserContext } from 'providers/managers/user-context-manager/user-context-manager';
import { useNavigate } from 'react-router-dom';
import { generateEditProposalPath } from 'routes/route-paths';
import OverviewContextMenuDeleteItem from './OverviewContextMenuDeleteItem';
import { PromptData } from 'modules/new-business/types/proposal/ContextMenuSharedTypes.type';
import { ProposalPrompt } from '../proposal-prompt/ProposalPrompt';

type OverviewContextMenuProps = {
  proposalId: number;
  isLocked: boolean;
  isDisabled: boolean;
};

export const OverviewContextMenu = ({
  proposalId,
  isLocked,
  isDisabled
}: OverviewContextMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [anchorElement, setAnchorElement] = useState<HTMLElement>();
  const userContextManagerApi = useUserContext();
  const navigate = useNavigate();

  const [action, setAction] = useState<() => () => Promise<void>>();
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [promptTitle, setPromptTitle] = useState('');
  const [promptText1, setPromptText1] = useState('');
  const [promptText2, setPromptText2] = useState('');
  const [primaryButtonText, setPrimaryButtonText] = useState('');
  const [secondaryButtonText, setSecondaryButtonText] = useState('');

  const goToProposal = () => {
    navigate(generateEditProposalPath(proposalId));
  };

  const showPromptModal = (promptData: PromptData) => {
    setPromptTitle(promptData.promptTitle);
    setPromptText1(promptData.promptText1);
    setPromptText2(promptData.promptText2);
    setPrimaryButtonText(promptData.primaryButtonText);
    setSecondaryButtonText(promptData.secondaryButtonText);
    setAction(promptData.action);
    setIsOpen(false);
    setIsPromptOpen(true);
  };

  const isEditProposalAllowed = userContextManagerApi.canAccessAll([
    SecurityAccessFeaturesEnum.EditProposal
  ]);
  const isDeleteProposalAllowed = userContextManagerApi.canAccessAll([
    SecurityAccessFeaturesEnum.DeleteProposal
  ]);
  const isViewProposalAllowed = isLocked || !isEditProposalAllowed;

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Needed to override the base row click event to prevent double clicks.
    <div onClick={(event) => event.stopPropagation()}>
      <ProposalPrompt
        action={action}
        isPromptOpen={isPromptOpen}
        promptText1={promptText1}
        promptText2={promptText2}
        title={promptTitle}
        setIsPromptOpen={setIsPromptOpen}
        primaryButtonText={primaryButtonText}
        secondaryButtonText={secondaryButtonText}
      />
      <IconButton
        aria-label='Accessible label'
        size={IconButton.SIZE.X_SMALL}
        variant={IconButton.VARIANT.TERTIARY}
        disabled={isDisabled}
        onClick={(event) => {
          setAnchorElement(event.nativeEvent.target as HTMLElement);
          setIsOpen(true);
          event.stopPropagation();
        }}
      >
        <Icon icon='navigation_more-vert' />
      </IconButton>
      <Menu
        aria-label={`Proposal ${proposalId} menu`}
        open={isOpen}
        onClose={() => setIsOpen(false)}
        anchorEl={anchorElement}
        position={Menu.POSITION.BOTTOM_END}
        offset={[0, -180]}
        maxHeight={-1}
        minWidth={210}
        maxWidth={210}
        preventFocusOnOpen={true}
        closeOnScroll={true}
      >
        {isViewProposalAllowed && (
          <OverviewContextMenuItem
            onClick={goToProposal}
            label='View'
            disabled={isDisabled}
            icon={<Icon icon='action_visibility' />}
          />
        )}

        {isDeleteProposalAllowed && !isLocked && !isDisabled && (
          <OverviewContextMenuDeleteItem
            proposalId={proposalId}
            disabled={!isDeleteProposalAllowed || isLocked || isDisabled}
            handleClick={showPromptModal}
          />
        )}

        <OverviewContextMenuItem
          onClick={goToProposal}
          label='Edit'
          disabled={isLocked || isDisabled || !isEditProposalAllowed}
          icon={<Icon icon='editor_mode' />}
        />
      </Menu>
    </div>
  );
};
